import React from 'react';
import { Loader2, AlertCircle, Tag } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * ClassificationBlock — Klassifizierungsmerkmale (Phase 3a).
 * Block wird ausgeblendet wenn keine Merkmale vorhanden und kein Fehler.
 *
 * Props:
 *   classifications   Array|null   Ergebnis von getLagerClassifications
 *   loading           boolean
 *   error             string|null
 */
/**
 * Einheit nur anhängen, wenn der Anzeigewert sie nicht bereits enthält.
 * Hintergrund: merkmal_value (AUSP-ATWRT) wird als vollständiger Anzeigetext
 * geliefert und trägt die Einheit meist schon (z.B. "10 m", "2,5 mm²",
 * "16 GB DDR4"). Ohne diese Prüfung entstünde eine Doppelung ("10 m m",
 * "2,5 mm² mm²"). Liefert SAP künftig einen reinen Zahlenwert ohne Einheit,
 * wird die Einheit weiterhin korrekt angehängt.
 */
const formatMerkmalValue = (value, unit) => {
  const val = value == null ? '' : String(value).trim();
  if (!unit) return val || '—';
  if (!val) return unit;
  return val.toLowerCase().includes(String(unit).toLowerCase()) ? val : `${val} ${unit}`;
};

const ClassificationBlock = ({ classifications, loading, error }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const cellMuted   = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const cellVal     = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const sectionTitle = `text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-brand' : 'text-brand'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.classificationLoading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-3">
        <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.classificationError')}: {error}</span>
      </div>
    );
  }

  // Block ausblenden wenn keine Merkmale vorhanden (kein Fehler, kein Loading)
  if (!Array.isArray(classifications) || classifications.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Tag className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
        <p className={sectionTitle}>{t('lm.classificationTitle')}</p>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {classifications.map((item, idx) => (
          <div key={item.merkmal_name ?? idx} className="flex gap-2 min-w-0">
            <dt className={`text-xs shrink-0 ${cellMuted}`}>
              {item.merkmal_name ?? '—'}:
            </dt>
            <dd className={`text-xs font-medium truncate ${cellVal}`}>
              {formatMerkmalValue(item.merkmal_value, item.unit)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default ClassificationBlock;
